import React, { useState } from 'react';
import { Anchor, Checkbox, Group, Stack, Text, Textarea, TextInput } from '@mantine/core';
import { useIntl } from 'react-intl';

import { PICKUP_SERVICE_FIELDS, ServicesGrid } from '@shipstation/client-common/components/freight-booking-wizard';
import { shipFromStepMessages } from '@shipstation/client-common/components/freight-booking-wizard/components/ship-from-step/ship-from-step-messages';
import { EMPTY_LTL_DEFAULTS, normalizeLtlPickupWindow, toWireTime, toWizardTime } from '@shipstation/client-common/ltl/ltl-defaults';

import { useShipFromContext } from '../../contexts/ship-from-context';
import { shipFromFreightDefaultsMessages } from './ship-from-freight-defaults-messages';

import type { LtlDefaults, LtlPickupServices } from '@shipstation/client-common/types/ship-from';

/**
 * "Freight Defaults" section rendered in the Ship From Location modal sidebar.
 * Reads from and writes to the ShipFrom context via updateShipFrom.
 * All writes funnel through normalizeLtlPickupWindow to enforce AC6.
 *
 * Layout matches loc3.png (Freight Defaults tab):
 *   - Pickup Ready Time / Pickup Close Time (side by side)
 *   - "I will arrange a pickup with the carrier separately" checkbox
 *   - Pickup Services header
 *   - 9-checkbox 2-column ServicesGrid
 *   - "Add Pickup Instructions" toggle link → reveals textarea
 */
/** Narrow slice of the ShipFromContext used by this component. */
type ShipFromContextSlice = {
  shipFrom?: { ltlDefaults?: LtlDefaults | null };
  updateShipFrom?: (patch: { ltlDefaults: LtlDefaults }) => void;
};

export const ShipFromFreightDefaults = () => {
  const intl = useIntl();
  const ctx = useShipFromContext() as ShipFromContextSlice | null;
  const shipFrom = ctx?.shipFrom;
  const updateShipFrom = ctx?.updateShipFrom;

  const ltlDefaults: LtlDefaults = shipFrom?.ltlDefaults ?? EMPTY_LTL_DEFAULTS;

  // "Add Pickup Instructions" toggle — visible by default if instructions already exist
  const [showInstructions, setShowInstructions] = useState(!!ltlDefaults.pickupInstructions);

  const updateDefaults = (next: LtlDefaults) => {
    const forNormalize = {
      ...next,
      pickupReadyTime: next.pickupReadyTime ?? '',
      pickupCloseTime: next.pickupCloseTime ?? '',
    };
    const normalized = normalizeLtlPickupWindow(forNormalize);
    updateShipFrom?.({
      ltlDefaults: {
        ...next,
        pickupReadyTime: normalized.pickupReadyTime || null,
        pickupCloseTime: normalized.pickupCloseTime || null,
        willArrangePickupSeparately: normalized.willArrangePickupSeparately,
      },
    });
  };

  const handleArrangeSeparatelyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    updateDefaults({
      ...ltlDefaults,
      willArrangePickupSeparately: event.currentTarget.checked,
      ...(event.currentTarget.checked ? { pickupReadyTime: null, pickupCloseTime: null } : {}),
    });
  };

  const handleServicesChange = (next: LtlPickupServices) => {
    updateDefaults({ ...ltlDefaults, pickupServices: next });
  };

  const handleReadyTimeChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    updateDefaults({ ...ltlDefaults, pickupReadyTime: toWireTime(event.currentTarget.value) });

  const handleCloseTimeChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    updateDefaults({ ...ltlDefaults, pickupCloseTime: toWireTime(event.currentTarget.value) });

  return (
    <Stack gap='md'>
      <Group grow>
        <TextInput
          data-testid='freight-defaults-pickup-ready-time'
          disabled={ltlDefaults.willArrangePickupSeparately}
          label={intl.formatMessage(shipFromStepMessages.pickupReadyTimeLabel)}
          onChange={handleReadyTimeChange}
          type='time'
          value={toWizardTime(ltlDefaults.pickupReadyTime)}
        />
        <TextInput
          data-testid='freight-defaults-pickup-close-time'
          disabled={ltlDefaults.willArrangePickupSeparately}
          label={intl.formatMessage(shipFromStepMessages.pickupCloseTimeLabel)}
          onChange={handleCloseTimeChange}
          type='time'
          value={toWizardTime(ltlDefaults.pickupCloseTime)}
        />
      </Group>

      <Checkbox
        checked={ltlDefaults.willArrangePickupSeparately}
        data-testid='freight-defaults-arrange-separately'
        label={intl.formatMessage(shipFromStepMessages.arrangePickupSeparately)}
        onChange={handleArrangeSeparatelyChange}
      />

      <Text fw={500} size='sm'>
        {intl.formatMessage(shipFromStepMessages.pickupServicesHeader)}
      </Text>

      <ServicesGrid fields={PICKUP_SERVICE_FIELDS} onChange={handleServicesChange} value={ltlDefaults.pickupServices} />

      {/* "Add Pickup Instructions" — toggle link matching loc3.png design */}
      {showInstructions ? (
        <Textarea
          autoFocus
          data-testid='freight-defaults-pickup-instructions'
          label={intl.formatMessage(shipFromFreightDefaultsMessages.pickupInstructionsLabel)}
          maxLength={500}
          onChange={(event) => updateDefaults({ ...ltlDefaults, pickupInstructions: event.currentTarget.value || null })}
          placeholder={intl.formatMessage(shipFromFreightDefaultsMessages.pickupInstructionsPlaceholder)}
          value={ltlDefaults.pickupInstructions ?? ''}
        />
      ) : (
        <Anchor
          component='button'
          data-testid='freight-defaults-add-instructions-link'
          onClick={() => setShowInstructions(true)}
          size='sm'
          type='button'
        >
          {intl.formatMessage(shipFromFreightDefaultsMessages.addPickupInstructionsLink)}
        </Anchor>
      )}
    </Stack>
  );
};
